import { isDlsDeviceName, isDlsSensorExpired } from '../common'
import type { StatusTone } from '../themes/statusColors'
import type { Device, Pet } from '../types'

/**
 * Derives a "Data Status" tag for a pet, used by:
 *   - PetList "Data Status" column (one-glance triage for sales/support/vet)
 *   - PetShow empty-state messaging
 *
 * Replaces the old binary view ("has device with metrics" vs "hidden") that
 * caused the Clinic Directory bug — pets that were linked-but-data-light got
 * hidden from the list entirely, so the directory's "Linked pets: 3" looked
 * like a counting error. The fix is to surface those pets with a status tag
 * so the clinic sees what state each pet is in instead of just "missing."
 */
export type PetDataStatus =
  | 'active' // has device with metrics, last reading within the freshness window
  | 'waiting_sync' // has device(s), but none have metrics yet — sensor paired, not synced
  | 'no_sensor' // no devices linked at all
  | 'data_error' // has device with metrics but reading stalled past freshness window
  | 'expired' // current DLS sensor is past its 14d+6h wear life (vic/vetpo#63)
  | 'archived' // soft-deleted (status = -1)

export interface PetDataStatusInput {
  pet: Pick<Pet, 'id'> & Partial<Pet> & { status?: number | null }
  devices: ReadonlyArray<Device>
  devicesWithMetrics: ReadonlySet<number> | null
  /** Unix-seconds timestamp of latest metric for the device, if known. */
  latestMetricTime: number | null
  /** "Now" in unix seconds — injectable for tests. */
  nowSeconds?: number
}

// Anything older than 24h on an otherwise-data-bearing device counts as
// "stalled." Picked to match what the Ops dashboard treats as "device gone
// quiet" — a clinic looking at the list should worry about pets whose
// readings stopped a day ago, not pets that just took an hour-long gap.
const FRESHNESS_WINDOW_SECONDS = 24 * 60 * 60

export const derivePetDataStatus = ({
  pet,
  devices,
  devicesWithMetrics,
  latestMetricTime,
  nowSeconds,
}: PetDataStatusInput): PetDataStatus => {
  if (pet?.status === -1) return 'archived'

  const petId = pet?.id
  const petDevices = devices.filter((d) => {
    const linkedId = typeof d.pet === 'object' ? d.pet?.id : d.pet
    return linkedId === petId
  })

  if (petDevices.length === 0) return 'no_sensor'

  // The "current" sensor is the one with the latest start_time — a superseded
  // device that already got replaced should not flag the pet as expired.
  // Once ada-core#293 filters is_expired readings out of every read, an
  // expired-but-still-transmitting DLS sensor would otherwise fall through to
  // waiting_sync (its non-expired metrics have aged out of the membership
  // check) or data_error (last reading stalls) — both misread as a
  // connectivity problem rather than "replace the sensor" (vic/vetpo#63).
  const currentDevice = petDevices.reduce<Device | null>((latest, d) => {
    if (typeof d.start_time !== 'number' || !Number.isFinite(d.start_time) || d.start_time <= 0) {
      return latest
    }
    if (!latest || (latest.start_time ?? 0) < d.start_time) return d
    return latest
  }, null)

  // A sensor registered AFTER the current one started, with no start_time yet,
  // is a replacement whose wear we cannot evaluate: fail OPEN for the pet, as
  // isDlsSensorExpired does for one device. Picking "current" by start_time
  // alone dropped it and let the old sensor say "replace sensor" after the
  // owner already had (eng-brain P72-1).
  const hasNewerUnanchoredDevice =
    currentDevice != null &&
    petDevices.some(
      (d) =>
        d !== currentDevice &&
        !(typeof d.start_time === 'number' && Number.isFinite(d.start_time) && d.start_time > 0) &&
        Date.parse(d.createdAt) / 1000 > (currentDevice.start_time ?? 0),
    )

  const now = nowSeconds ?? Math.floor(Date.now() / 1000)
  if (
    currentDevice &&
    !hasNewerUnanchoredDevice &&
    isDlsDeviceName(currentDevice.name) &&
    isDlsSensorExpired(currentDevice.start_time, now)
  ) {
    return 'expired'
  }

  // devicesWithMetrics === null means the metrics-presence check failed; fall
  // back to "we don't know, assume waiting" rather than mis-tagging as active.
  if (devicesWithMetrics === null) return 'waiting_sync'

  const anyHasMetrics = petDevices.some((d) => devicesWithMetrics.has(d.id))
  if (!anyHasMetrics) return 'waiting_sync'

  // Active vs. data_error: needs a latest-reading timestamp. Without one,
  // we know SOMETHING uploaded so we lean active rather than alarming.
  if (latestMetricTime == null) return 'active'
  if (now - latestMetricTime > FRESHNESS_WINDOW_SECONDS) return 'data_error'
  return 'active'
}

export const petDataStatusColor = (
  status: PetDataStatus,
): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active':
      return 'success'
    case 'waiting_sync':
      return 'warning'
    case 'no_sensor':
      return 'default'
    // Amber, not red. Red is reserved for real medical alerts (UI-STYLE-GUIDE:
    // color = clinical priority). A sensor going quiet is overwhelmingly a
    // FINISHED 14-day wear — VetSP's A0009VC ran 2026-07-09 → 07-23 to the day
    // and then sat red as "Data Error" for a month, which is what the clinic
    // called support about — so painting every completed sensor in alert red
    // trains vets to ignore red.
    case 'data_error':
      return 'warning'
    // Also amber, not red: expiry is an expected end-of-wear event with a
    // clear next action ("replace sensor"), not a medical alert.
    case 'expired':
      return 'warning'
    case 'archived':
      return 'default'
  }
}

/**
 * The chip tone for a status, derived from petDataStatusColor (the spec'd
 * VP-PATIENTS §3 mapping) so there is still ONE decision: success -> the quiet
 * positive tone, warning -> the quiet attention tone, default -> neutral
 * (UI-STYLE-GUIDE §3). MUI's palette versions were saturated filled/outlined
 * chips on every pet row.
 */
export const petDataStatusTone = (status: PetDataStatus): StatusTone => {
  switch (petDataStatusColor(status)) {
    case 'success':
      return 'positive'
    case 'warning':
      return 'attention'
    case 'error':
      return 'critical'
    default:
      return 'neutral'
  }
}
